import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FormState, FieldDefinition, ValidationRule } from './edit-modal/types';
import { findCardInSequence } from './utils';
import { validateForm } from './edit-modal/validation';
import { fieldRegistry } from './edit-modal/fieldRegistry';
import './edit-modal/registerFields';
import { useEditorStore } from '~/shell/Store';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { Overlay } from '~/ui/overlay/Overlay';
import {
  ModalWrapper,
  ModalContent,
  ModalHeader,
  Title,
  CloseButton,
  Form,
  Fields,
  ButtonRow,
  SubmitButton
} from './edit-modal/sharedComponents';
import { Modal } from '~/ui/modal/Modal';
import CloseIcon from '~/assets/icons/close.svg';
import { GLOBAL_PLACEHOLDER } from './templates/constants';
import { useTemplateTextFields } from '~/training/sequence/templates/utils/templateTextFields';

interface CardModalProps {
  mode: 'create' | 'edit';
  open: boolean;
  onClose: () => void;
  cardId?: string | number;
  fields: FieldDefinition[];
  validation?: ValidationRule;
  modalTitle: string;
  onCreate?: (data: Record<string, unknown>) => void;
}

export const CardModal: React.FC<CardModalProps> = ({
  mode,
  open,
  onClose,
  cardId,
  fields,
  validation,
  modalTitle: modalTitleProp,
  onCreate
}) => {
  const {
    sequence,
    updateSubItemBatch,
    updateFile,
    editorMode,
    setForceUpdateSequence,
    globalPlaceholders
  } = useEditorStore();

  const cardItem = useMemo(() => {
    if (mode === 'create') return null;
    if (!cardId) return null;
    return findCardInSequence(sequence!, cardId);
  }, [sequence, cardId, mode]);

  const [formState, setFormState] = useState<FormState>({});
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  const hasInitialized = useRef(false);

  /** Closes the panel, unless a save is on its way: its answer decides. */
  const closeUnlessSaving = () => {
    if (!savingRef.current) onClose();
  };

  // Template text fields are offered only to a company with the flag; the
  // panel draws and saves only the fields it offers.
  const templateTextFields = useTemplateTextFields();
  const effectiveConfig = useMemo(() => {
    return {
      fields: fields.filter(
        field => templateTextFields || !field.templateTextField
      ),
      validation,
      modalTitle: modalTitleProp
    };
  }, [fields, validation, modalTitleProp, templateTextFields]);

  useEffect(() => {
    if (!open) {
      hasInitialized.current = false;
      return;
    }

    // Each field starts from the card (or its default) once: when the panel
    // opens, or when it is first offered while open (the company's flag
    // arrived). What the author typed in the other fields stays.
    const opened = hasInitialized.current;
    const withStartValues = (current: FormState): FormState =>
      effectiveConfig.fields.reduce((state, field) => {
        if (opened && field.name in state) return state;
        let value: unknown;

        if (mode === 'create') {
          let defaultValue = field.defaultValue ?? null;

          if (defaultValue === GLOBAL_PLACEHOLDER) {
            const resolver = fieldRegistry.getPlaceholderResolver(field.type);
            defaultValue = (
              resolver
                ? resolver(
                    field,
                    globalPlaceholders as unknown as Record<string, unknown>
                  )
                : null
            ) as typeof defaultValue;
          }

          value = defaultValue;
        } else {
          if (!cardItem) return state;

          value = fieldRegistry.extractValue(field, cardItem);
        }

        return { ...state, [field.name]: value as FormState[string] };
      }, current);

    setFormState(current => withStartValues(opened ? current : {}));
    hasInitialized.current = true;
  }, [mode, cardItem, open, effectiveConfig, globalPlaceholders]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !savingRef.current) {
        onClose();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const modalTitle = modalTitleProp || effectiveConfig.modalTitle;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current) return;

    const validationError = validateForm(effectiveConfig, formState);
    if (validationError) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: validationError
      });
      return;
    }

    if (mode === 'create') {
      if (!onCreate) return;

      const data: Record<string, unknown> = {};

      effectiveConfig.fields.forEach(field => {
        const currentValue = formState[field.name];
        const serializer = fieldRegistry.getApiSerializer(field.type);
        if (serializer) {
          serializer(field, currentValue, data);
        }
      });

      onCreate(data);

      onClose();
      return;
    }

    if (!cardId) return;

    const updates: Record<string, unknown> = {};
    // New media are saved once the text saved with them is accepted.
    const mediaSaves: Array<() => void> = [];

    effectiveConfig.fields.forEach(field => {
      const currentValue = formState[field.name];
      const updateHandler = fieldRegistry.getUpdateHandler(field.type);

      if (updateHandler) {
        updateHandler(
          field,
          currentValue,
          cardItem as Record<string, unknown>,
          String(cardId),
          {
            updateFile: (id, media, updateType) => {
              mediaSaves.push(() =>
                updateFile(
                  id,
                  media as Parameters<typeof updateFile>[1],
                  updateType
                )
              );
            },
            addToBatch: (fieldName, value) => {
              updates[fieldName] = value;
            }
          }
        );
      }
    });

    if (Object.keys(updates).length > 0) {
      savingRef.current = true;
      setIsSaving(true);
      const result = await updateSubItemBatch(cardId, updates);
      savingRef.current = false;
      setIsSaving(false);
      // Refused: the draft stays in the panel to correct and save again.
      if (result?.error) {
        return;
      }
    }

    mediaSaves.forEach(save => save());
    setForceUpdateSequence(true);
    onClose();
  };

  if (!open || !editorMode) {
    return null;
  }

  const modalContent = (
    <Overlay open={true} opacity={0.5} onClick={closeUnlessSaving}>
      <ModalWrapper
        onClick={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
      >
        <Modal
          open={true}
          width="462px"
          horizontal="center"
          vertical="center"
          borderRadius="20px"
        >
          <ModalContent>
            <ModalHeader>
              <Title>{modalTitle}</Title>
              <CloseButton
                onClick={closeUnlessSaving}
                aria-label="Close card modal"
              >
                <CloseIcon />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit} aria-busy={isSaving}>
              <Fields disabled={isSaving}>
                {effectiveConfig.fields.map(field => {
                  const contextProvider = fieldRegistry.getContextProvider(
                    field.type
                  );
                  const context = contextProvider
                    ? contextProvider(
                        field,
                        cardItem as Record<string, unknown>
                      )
                    : {};

                  return fieldRegistry.render(field, formState, setFormState, {
                    ...context,
                    cardId
                  });
                })}

                <ButtonRow>
                  <SubmitButton type="submit">
                    {isSaving ? 'Saving…' : 'Submit'}
                  </SubmitButton>
                </ButtonRow>
              </Fields>
            </Form>
          </ModalContent>
        </Modal>
      </ModalWrapper>
    </Overlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
